import { isValidHex, contrastRatio, shadeHex, mixHex, ensureContrast } from './colorUtil';
import { formatHourLabel, parseTimeSlotStartHour, getCourtRateBands, getCourtLabel } from './bookingUtils';
import { formatMoney } from './currencyUtil';
import {
  RATE_SEPARATOR_SHOW_CONFIG_KEY,
  RATE_SEPARATOR_DESIGN_CONFIG_KEY,
  RATE_SEPARATOR_DESIGN_OPTIONS,
  DEFAULT_RATE_SEPARATOR_DESIGN,
} from '../constants/bookingConst';

/**
 * Whether the time slot grids show rate separators (Config -> Sports &
 * Courts -> Hourly rates). Read as "not switched off", so a config saved before the
 * setting existed keeps showing them.
 *
 * @param {object} config
 * @returns {boolean}
 */
export const isRateSeparatorShown = (config) => config?.[RATE_SEPARATOR_SHOW_CONFIG_KEY] !== false;

/**
 * The separator design in use, falling back to the default for a missing or
 * unknown value.
 *
 * @param {object} config
 * @returns {string} a RATE_SEPARATOR_DESIGNS value
 */
export const resolveRateSeparatorDesign = (config) => {
  const value = config?.[RATE_SEPARATOR_DESIGN_CONFIG_KEY];
  return RATE_SEPARATOR_DESIGN_OPTIONS.some(option => option.value === value)
    ? value
    : DEFAULT_RATE_SEPARATOR_DESIGN;
};

/**
 * The separator setting as one readable line for the audit trail, e.g.
 * "Shown \u00b7 Design 2 \u2013 Bar" or "Hidden". Comparing two of these also
 * tells handleSave whether the setting changed at all.
 *
 * @param {object} config
 * @returns {string}
 */
export const describeRateSeparatorSetting = (config) => {
  if (!isRateSeparatorShown(config)) return 'Hidden';
  const design = resolveRateSeparatorDesign(config);
  const option = RATE_SEPARATOR_DESIGN_OPTIONS.find(item => item.value === design);
  return `Shown \u00b7 ${option ? option.label : design}`;
};

/**
 * The hours printed against a rate band, e.g. "4:00 PM - 2:00 AM".
 *
 * A band whose start and end hour are the SAME is the "covers every hour"
 * convention (see isHourInBand in bookingUtils) rather than a real range, so
 * printing it literally gave "12:00 MN - 12:00 MN". Such a band borrows the
 * hours the grid actually draws (its first and last row) instead. Compared on
 * the clock hour each value PRINTS as, since a whole-day band can be stored as
 * 0 -> 0 or as 0 -> 24.
 *
 * @param {{startHour: number, endHour: number}} band
 * @param {Array<{slot: string}>} rows - the grid's hour rows, in display order
 * @param {string} [sep] - what goes between the two hours
 * @returns {string}
 */
export const formatRateBandHours = (band, rows, sep = ' - ') => {
  const clockHour = (hour) => (((Number(hour) || 0) % 24) + 24) % 24;
  const start = clockHour(band?.startHour);
  const end = clockHour(band?.endHour);
  // A real range names its own hours; a closed day has none to borrow.
  if (start !== end || !rows?.length) {
    return `${formatHourLabel(start)}${sep}${formatHourLabel(end)}`;
  }
  const openHour = parseTimeSlotStartHour(rows[0].slot);
  const closeHour = (parseTimeSlotStartHour(rows[rows.length - 1].slot) + 1) % 24;
  return `${formatHourLabel(openHour)}${sep}${formatHourLabel(closeHour)}`;
};

/**
 * The price shown on a rate separator: one price when every court charges the
 * same for that rate, otherwise each court with its own. Read through
 * getCourtRateBands — the same resolver as the rates strip and checkout,
 * promos included — for the date of the row the separator heads.
 *
 * @param {object} config
 * @param {Array<number>} courts - the court numbers the grid shows
 * @param {string|null} sportId - the sport the grid shows
 * @param {object} band - the rate band
 * @param {string} dateStr - 'yyyy-MM-dd'
 * @returns {string} e.g. "₱250/hr", or "Court 1, Court 3 ₱250/hr · Court 2 ₱300/hr"
 */
export const getRateSeparatorPriceText = (config, courts, sportId, band, dateStr) => {
  const courtsByPrice = new Map();
  (courts || []).forEach((court) => {
    const entry = getCourtRateBands(config, court, sportId || null, dateStr)
      .find(item => item.band.id === band.id);
    if (!entry) return;
    if (!courtsByPrice.has(entry.price)) courtsByPrice.set(entry.price, []);
    courtsByPrice.get(entry.price).push(court);
  });
  const groups = [...courtsByPrice.entries()];
  if (groups.length <= 1) {
    const price = groups.length ? groups[0][0] : Number(band.price) || 0;
    return `${formatMoney(price, config)}/hr`;
  }
  return groups
    .map(([price, list]) => `${list.map(court => getCourtLabel(config, court)).join(', ')} ${formatMoney(price, config)}/hr`)
    .join(' \u00b7 ');
};

/**
 * Where the rate separators sit in a time slot grid (guest and admin).
 *
 * Walks the rows in display order and puts a separator in front of every row
 * whose rate band differs from the row above it (and in front of the first
 * row), so each rate the admin set up in Config -> Sports & Courts -> Hourly
 * rates gets a heading where it starts. A row no band covers gets no
 * separator of its own.
 *
 * The grid places every cell on an explicit row line (block bookings span
 * several rows), so this also returns the line each hour row lands on once
 * the separator rows are slotted in between them.
 *
 * @param {Array<object>} rows - the grid's hour rows, in display order
 * @param {(row: object) => (object|null)} getBand - the band covering a row
 * @param {number} [firstLine] - grid line of the first row (line 1 is the court headers)
 * @returns {{separators: Map<number, {band: object, line: number}>, rowLines: number[]}}
 *   separators keyed by the index of the row they head; rowLines[i] is row i's line
 */
export const planRateSeparators = (rows, getBand, firstLine = 2) => {
  const separators = new Map();
  const rowLines = [];
  let line = firstLine;
  let previousId;

  (rows || []).forEach((row, index) => {
    const band = getBand(row) || null;
    const id = band ? band.id : null;
    if (band && id !== previousId) {
      separators.set(index, { band, line });
      line += 1;
    }
    previousId = id;
    rowLines.push(line);
    line += 1;
  });

  return { separators, rowLines };
};

/**
 * Colours for a separator pill with an admin-chosen background: the text is
 * whichever of near-white or near-black reads better on it, and the border is
 * a slightly darker shade so a pale colour still has an edge.
 *
 * @param {string} [color] - the band's `color`, a hex value
 * @returns {{background: string, color: string, borderColor: string}|null}
 *   null when no valid colour is set — the pill then uses its default theme colours
 */
export const getRateSeparatorColors = (color) => {
  if (!isValidHex(color)) return null;
  const background = color.trim();
  const light = '#ffffff';
  const dark = '#1f1f1f';
  return {
    background,
    color: contrastRatio(background, light) >= contrastRatio(background, dark) ? light : dark,
    borderColor: shadeHex(background, -15),
  };
};

/**
 * Colours for a Design 2 separator bar built from the band's colour: a pale
 * tint of it for the bar, a slightly stronger tint for the price badge, and
 * the colour itself (darkened if needed to stay readable on the
 * badge) for the icon and price.
 *
 * @param {string} [color] - the band's `color`, a hex value
 * @returns {{background: string, badgeBackground: string, accent: string}|null}
 *   null when no valid colour is set — the bar then uses the club's accent
 */
export const getRateSeparatorBarColors = (color) => {
  if (!isValidHex(color)) return null;
  const base = color.trim();
  const badgeBackground = mixHex(base, '#ffffff', 0.8);
  return {
    background: mixHex(base, '#ffffff', 0.9),
    badgeBackground,
    accent: ensureContrast(base, badgeBackground, 4.5, '#000000'),
  };
};

